import React, { useState, useMemo } from 'react';
import { Select, Row, Col, Spin, Button, Radio, Tabs, Descriptions, Tag, Card } from 'antd';
import {
  ReloadOutlined, DashboardOutlined, CheckCircleOutlined,
  CloseCircleOutlined, ClockCircleOutlined, CloudServerOutlined,
  HddOutlined, WifiOutlined, ThunderboltOutlined,
} from '@ant-design/icons';
import {
  useListServersQuery, useCpuHistoryQuery, useRamHistoryQuery,
  useDiskHistoryQuery, useNetworkHistoryQuery,
} from '../store/api';
import CPUChart from '../components/CPUChart';
import RAMChart from '../components/RAMChart';
import DiskIOChart from '../components/DiskIOChart';
import NetworkChart from '../components/NetworkChart';

const { Option } = Select;

const MetricasServidor = () => {
  const { data: servidores = [] } = useListServersQuery();
  const [servidorSeleccionado, setServidorSeleccionado] = useState(null);
  const [dias, setDias] = useState(1);

  const queryArgs = { dias, id_servidor: servidorSeleccionado || '' };
  const skip = servidores.length === 0;

  const { data: cpuRaw, isFetching: loadingCPU, refetch: refetchCpu } = useCpuHistoryQuery(queryArgs, { skip });
  const { data: ramRaw, isFetching: loadingRAM, refetch: refetchRam } = useRamHistoryQuery(queryArgs, { skip });
  const { data: discoRaw, isFetching: loadingDisco, refetch: refetchDisco } = useDiskHistoryQuery(queryArgs, { skip });
  const { data: redRaw, isFetching: loadingRed, refetch: refetchRed } = useNetworkHistoryQuery(queryArgs, { skip });

  const cpuData = cpuRaw?.success ? cpuRaw.servidores : [];
  const ramData = ramRaw?.success ? ramRaw.servidores : [];
  const discoData = discoRaw?.success ? discoRaw.servidores : [];
  const redData = redRaw?.success ? redRaw.servidores : [];

  const loading = loadingCPU || loadingRAM || loadingDisco || loadingRed;
  const servidor = useMemo(() => servidores.find(s => s.id === servidorSeleccionado), [servidores, servidorSeleccionado]);

  const handleRefresh = () => {
    refetchCpu(); refetchRam(); refetchDisco(); refetchRed();
  };

  const renderMetricTab = (data, isLoading, Component, emptyMsg) => {
    if (isLoading) return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <Spin size="large" />
        <p style={{ marginTop: 16, color: '#8392a5' }}>Cargando datos...</p>
      </div>
    );
    if (data.length === 0) return (
      <div className="chart-container" style={{ textAlign: 'center', padding: 60, color: '#8392a5' }}>{emptyMsg}</div>
    );
    return data.map(srv => <Component key={srv.id} servidor={srv} height={350} />);
  };

  const periodOptions = [
    { label: 'Hoy', value: 1 },
    { label: '7 días', value: 7 },
    { label: '30 días', value: 30 },
  ];

  return (
    <div className="page-container fade-in">
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title"><DashboardOutlined style={{ marginRight: 8 }} />Métricas del Sistema</h1>
          <div className="page-subtitle">Monitoreo en tiempo real de recursos</div>
        </div>
        <Button icon={<ReloadOutlined />} onClick={handleRefresh} loading={loading} style={{ borderRadius: 8, fontWeight: 600 }}>
          Actualizar
        </Button>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={24} sm={12}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#8392a5', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.5px' }}>
            Servidor
          </label>
          <Select style={{ width: '100%' }} placeholder="Todos los servidores" allowClear
            value={servidorSeleccionado} onChange={setServidorSeleccionado} size="large">
            {servidores.map(srv => <Option key={srv.id} value={srv.id}>{srv.nombre} ({srv.ip})</Option>)}
          </Select>
        </Col>
        <Col xs={24} sm={12}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#8392a5', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.5px' }}>
            Período
          </label>
          <Radio.Group value={dias} onChange={e => setDias(e.target.value)} buttonStyle="solid" size="large">
            {periodOptions.map(opt => <Radio.Button key={opt.value} value={opt.value}>{opt.label}</Radio.Button>)}
          </Radio.Group>
        </Col>
      </Row>

      {servidor && (
        <Card bordered={false} style={{ marginBottom: 20, borderRadius: 12 }}>
          <Descriptions
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <CloudServerOutlined />
                <span style={{ fontWeight: 700 }}>{servidor.nombre}</span>
                <Tag color={servidor.sincronizacion === '1' ? 'success' : 'error'} style={{ borderRadius: 12 }}>
                  {servidor.sincronizacion === '1' ? <><CheckCircleOutlined /> Sincronizado</> : <><CloseCircleOutlined /> Desincronizado</>}
                </Tag>
              </div>
            }
            bordered size="small" column={{ xxl: 6, xl: 4, lg: 3, md: 3, sm: 2, xs: 1 }}>
            <Descriptions.Item label="IP"><code>{servidor.ip}</code></Descriptions.Item>
            <Descriptions.Item label="Usuario">{servidor.userserver}</Descriptions.Item>
            <Descriptions.Item label="Puerto SSH">{servidor.portaserver}</Descriptions.Item>
            <Descriptions.Item label="Espacio Libre">{servidor.espaciolibre}</Descriptions.Item>
            <Descriptions.Item label="Carga">{servidor.carga}</Descriptions.Item>
            <Descriptions.Item label="Uptime"><ClockCircleOutlined style={{ marginRight: 4 }} />{servidor.uptime || 'N/A'}</Descriptions.Item>
          </Descriptions>
        </Card>
      )}

      <Card bordered={false} style={{ borderRadius: 12 }} bodyStyle={{ padding: '8px 16px 16px' }}>
        <Tabs defaultActiveKey="cpu" size="large" items={[
          { key: 'cpu', label: <><ThunderboltOutlined /> CPU</>, children: renderMetricTab(cpuData, loadingCPU, CPUChart, 'No hay datos de CPU disponibles.') },
          { key: 'ram', label: <><HddOutlined /> RAM</>, children: renderMetricTab(ramData, loadingRAM, RAMChart, 'No hay datos de RAM disponibles.') },
          { key: 'disco', label: <><DashboardOutlined /> Disco I/O</>, children: renderMetricTab(discoData, loadingDisco, DiskIOChart, 'No hay datos de Disco I/O.') },
          { key: 'red', label: <><WifiOutlined /> Red</>, children: renderMetricTab(redData, loadingRed, NetworkChart, 'No hay datos de Red disponibles.') },
        ]} />
      </Card>
    </div>
  );
};

export default MetricasServidor;
